import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from './api.js';
import { pageStyles } from './components/cssstyles.js';
import { DataColumn, DataTableProvider } from './components/data-table.js';
import { contexts } from './contexts.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';
import { getInitials } from './utils.js';

const MUTED = 'color: var(--sl-color-neutral-500);';

// Links go to the provider account with the user's Flakiness login. The two
// usually match, since the login is copied from the provider on signup.
function createUsersProvider(gitlabOrigin: () => string): DataTableProvider<WireTypes.UserDetails, WireTypes.UserSortAxis> {
  const profileUrl = (provider: WireTypes.IdentityProvider, login: string) =>
    provider === 'github' ? `https://github.com/${login}` : `${gitlabOrigin().replace(/\/+$/, '')}/${login}`;

  const columns = {
    avatar: {
      sortAxis: undefined,
      renderHeader: () => html``,
      renderElement: user => html`
        <sl-avatar style="--size: 1.5rem; padding: var(--sl-spacing-x-small) 0;" image=${user.avatarUrl ?? ''} initials=${getInitials(user.userName)} loading=lazy></sl-avatar>
      `,
    },
    name: {
      sortAxis: 'name',
      sortOrder: ['asc', 'desc'],
      width: 'minmax(160px, 1fr)',
      renderHeader: () => html`Name`,
      renderElement: user => html`
        <h-box style="gap: var(--sl-spacing-2x-small); align-items: center;">
          <span>${user.userName}</span>
          ${user.isSuperUser ? html`<sl-badge variant=warning pill>superuser</sl-badge>` : nothing}
        </h-box>
      `,
    },
    login: {
      sortAxis: 'login',
      sortOrder: ['asc', 'desc'],
      renderHeader: () => html`Login`,
      // Users with both providers get one link per provider.
      renderElement: user => user.linkedProviders.length ? html`
        <v-box style="gap: 0;">
          ${user.linkedProviders.map(provider => html`
            <a
              href=${profileUrl(provider, user.userLogin)}
              target=_blank
              rel=noopener
              style="display: inline-flex; align-items: center; gap: var(--sl-spacing-2x-small);"
            ><sl-icon name=${provider}></sl-icon>${user.userLogin}</a>
          `)}
        </v-box>
      ` : html`<span>${user.userLogin}</span>`,
    },
    email: {
      sortAxis: 'email',
      sortOrder: ['asc', 'desc'],
      renderHeader: () => html`Email`,
      renderElement: user => user.email ? html`<span>${user.email}</span>` : html`<span style=${MUTED}>—</span>`,
    },
    lastSeen: {
      sortAxis: 'last_seen',
      renderHeader: () => html`Last visited`,
      renderElement: user => {
        if (user.lastSeenTimestampSeconds === undefined)
          return html`<span style=${MUTED} title="No visits recorded">—</span>`;
        const date = new Date(user.lastSeenTimestampSeconds * 1000).toISOString();
        return html`
          <sl-tooltip hoist>
            <sl-format-date slot=content month="long" day="numeric" year="numeric" hour="numeric" minute="numeric" hour-format="12" date=${date}></sl-format-date>
            <sl-relative-time date=${date}></sl-relative-time>
          </sl-tooltip>
        `;
      },
    },
  } satisfies Record<string, DataColumn<WireTypes.UserDetails, WireTypes.UserSortAxis>>;

  return {
    columns: Object.values(columns),
    defaultSortColumn: columns.lastSeen,
    loader: async (pageOptions, sortOptions, signal) => await api.administration.users.GET({
      sortOptions: sortOptions ?? { axis: 'last_seen', direction: 'desc' },
      pageOptions,
    }, { signal }),
  };
}

@customElement('page-administration-users')
export class PageAdministrationUsers extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/administration/users',
      ],
      render: () => html`<page-administration-users></page-administration-users>`,
    }];
  }

  static url(): string {
    return platformUrl('/administration/users');
  }

  @consume({ context: contexts.serverInfo, subscribe: true }) @state() private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  private _provider = createUsersProvider(() => this._serverInfo?.gitlabOrigin ?? 'https://gitlab.com');

  private _activeUsers = new Task(this, {
    args: () => [] as const,
    task: async ([], { signal }) => await api.administration.activeUsers.GET(undefined, { signal }),
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Users · Dashboard' }),
  });

  override render() {
    const activeUsers = this._activeUsers.value;
    const loading = html`<sl-skeleton effect=pulse></sl-skeleton>`;
    return html`
      <user-header submenu=admin-users></user-header>
      <app-body wide>
        <h-box style="align-items: end;">
          <h1>Users</h1>
          <x-filler></x-filler>
          <dl>
            <div class=metric>
              <dt>DAU (last 24 hours)</dt>
              <dd>${activeUsers ? activeUsers.dailyActiveUsers.toLocaleString() : loading}</dd>
            </div>
            <div class=metric>
              <dt>MAU (last 30 days)</dt>
              <dd>${activeUsers ? activeUsers.monthlyActiveUsers.toLocaleString() : loading}</dd>
            </div>
          </dl>
        </h-box>
        <data-table
          .provider=${this._provider}
          .pageNumber=${0}
          .pageSize=${50}
          .noDataMessage=${'No users'}
        ></data-table>
      </app-body>
      <app-footer></app-footer>
    `;
  }

  static styles = [pageStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }

    dl {
      display: flex;
      gap: var(--sl-spacing-2x-large);
      margin: 0;
    }

    .metric {
      display: flex;
      flex-direction: column;
    }

    dt {
      color: var(--sl-color-neutral-500);
    }

    dd {
      order: -1;
      margin: 0;
      font-size: var(--sl-font-size-2x-large);
      font-weight: var(--sl-font-weight-semibold);
      font-variant-numeric: tabular-nums;
      line-height: 1;
    }

    sl-skeleton {
      display: inline-block;
      width: 4ch;
      height: 1cap;
    }
  `];
}
